import { map } from "rxjs/operators";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { apiUrl } from "../app/app.runtime";
import { UserSettings } from "../components/models/UserSettings";
import { nfvStats } from "../components/models/nfv_stats";
import { ApiTokenOverrides, ModifyResponse } from "./self-service/models/types";
import { SelfServiceCommand } from "./self-service/self-service-command";
import { NWDUser, NWDUserConfig } from "./types";

@Injectable({
  providedIn: "root",
})
export class ApiHelper {
  private readonly http = inject(HttpClient);

  /**
   * createFetchOptions():object {} along with options = {observe: 'body'}
   * makes this.http return an object of <T> instead of HttpEvent<T>
   */
  createFetchOptions(
    options: object = {},
    responseType: string = "json",
    overrides?: ApiTokenOverrides,
    contentType: string = "application/json",
  ): object {
    const overrideToken = overrides !== undefined ? overrides.access_token : null;
    const access_token = overrideToken || "";

    let shouldIntercept = true;
    if (options && options["intercept"] === false) {
      shouldIntercept = false;
    }

    let contentHeaders = new HttpHeaders({
      "x-ignore-interceptor": shouldIntercept ? "0" : "1",
    });

    if (contentType !== "") {
      contentHeaders = contentHeaders.append("Content-Type", contentType);
    }

    // @deprecated! The token is handled via the TokenService.
    if (access_token !== "") {
      // only use this if an override token is presented.
      // in all other cases the correct token will be used in the NWDHttpInterceptor
      contentHeaders = contentHeaders.append("Authorization", `bearer ${access_token}`);
    }

    const fetchOptions = Object.assign({}, { params: {}, headers: contentHeaders, responseType }, options, {
      credentials: "same-origin",
      redirect: "manual",
      observe: "body",
    });
    return fetchOptions;
  }

  selfserviceStatusLatest(callback: (state: boolean) => void) {
    this.engineStatus().subscribe({
      next: ({ running }) => callback(running),
      error: () => callback(false),
    });
  }

  engineStatus() {
    return this.http.get<{ running: boolean }>(apiUrl("health/wfengine"));
  }

  config$() {
    const targetUrl = apiUrl("user/config");
    return this.http.get<NWDUserConfig>(targetUrl);
  }

  me$() {
    const targetUrl = apiUrl("user/me");
    const fetchOptions = this.createFetchOptions({ observe: "body" });
    return this.http.get<NWDUser>(targetUrl, fetchOptions);
  }

  settings() {
    const targetUrl = apiUrl("user/settings");
    const fetchOptions = this.createFetchOptions({ observe: "body" });
    return this.http
      .get<UserSettings>(targetUrl, fetchOptions)
      .pipe(map((data) => Object.assign(new UserSettings(), data)))
      .toPromise();
  }

  updateSettings(params: any) {
    const targetUrl = apiUrl("user/settings");
    const fetchOptions = this.createFetchOptions({ observe: "body" });
    return this.http
      .put<UserSettings>(targetUrl, params, fetchOptions)
      .pipe(map((data) => Object.assign(new UserSettings(), data)))
      .toPromise();
  }

  /**
   * Retrieve NFV stats
   */
  getNfvStats(subscriptionId: string, start: Date, end: Date): Promise<nfvStats> {
    let offsetUrlPart = "";
    if (start) {
      const deltaMinutes = Math.round((end.getTime() - start.getTime()) / 1000 / 60);
      const step = this.convertStep(deltaMinutes);
      const startDate = this.snapDate(start, step);
      const endDate = this.snapDate(end, step);
      offsetUrlPart += `?start=${startDate}&end=${endDate}&step=${step}`;
    } else {
      console.warn("Retrieving NFV stats without start date despite typing indicating otherwise.");
    }

    const endPointUrl = "stats/nfv/" + subscriptionId;
    const targetUrl = apiUrl(endPointUrl + offsetUrlPart);

    const toMilliseconds = ([miliseconds, data]: [number, number]): [number, number] => [miliseconds * 1000, data];

    return this.http
      .get(targetUrl, this.createFetchOptions({ intercept: false }))
      .toPromise()
      .then((data) => ({
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        cpuUsage: data["cpu_usage"].map(toMilliseconds),
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        memoryUsage: data["memory_usage"].map(toMilliseconds),
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        sessionCount: data["session_count"].map(toMilliseconds),
        // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
        sessionRate: data["session_rate"].map(toMilliseconds),
      }));
  }

  convertStep(deltaMinutes: number): number {
    const offsetMinutesToStep = {
      2880: 300,
      20160: 3600,
      86400: 14400,
      259200: 28800,
    }; // > 2days, 2 weeks, 2 months, 6 months
    let step = 60; // default for the first 2 days

    for (const key in offsetMinutesToStep) {
      if (deltaMinutes >= parseInt(key)) {
        step = offsetMinutesToStep[key];
      }
    }

    return step;
  }

  snapDate(date: Date, step: number) {
    const start = Math.round(date.getTime() / 1000);
    return start - (start % step);
  }

  convertStart(startOffsetMinutes: number, step: number) {
    const newDateObj = new Date();
    newDateObj.setTime(newDateObj.getTime() - startOffsetMinutes * 60 * 1000);
    const start = Math.round(newDateObj.getTime() / 1000);
    return start - (start % step);
  }

  public async set_customer_description(customerGUID: string, subscriptionId: string, value: string) {
    const targetUrl = apiUrl("customers/" + customerGUID + "/descriptions/" + subscriptionId);
    const fetchOptions = this.createFetchOptions();
    const parameters = {
      customerId: customerGUID,
      subscriptionId,
      description: value,
    };

    if (value === "") {
      console.log("Deleting the customer description");
      return this.http.delete(targetUrl, fetchOptions).toPromise();
    } else {
      console.log("Attempting to modify the description");
      try {
        return await this.http.put(targetUrl, parameters, fetchOptions).toPromise();
      } catch (_err) {
        console.log("Unable to modify changing the description");
        const targetUrl_1 = apiUrl("customers/descriptions");
        return await this.http.post(targetUrl_1, parameters, fetchOptions).toPromise();
      }
    }
  }

  /**
   * Send a request to customer support to report a problem
   *
   * @param customerGUID
   * @param fulfilmentData
   * @return Promise
   */
  add_new_fulfilment_problem(customerGUID: string, fulfilmentData: string) {
    const targetUrl = apiUrl("fulfilment/request/" + customerGUID);
    const fetchOptions = this.createFetchOptions({});

    return new Promise((resolve, reject) => {
      this.http
        .post(targetUrl, fulfilmentData, fetchOptions)
        .toPromise()
        .then(
          (_res) => {
            // Success
            resolve(true);
          },
          (msg) => {
            // Error
            if (msg.status === 422) {
              if (msg.error.detail) {
                reject(msg.error.detail[0].msg);
              }
            }
            reject(msg);
          },
        );
    });
  }

  /**
   * Upload a fulfilment attachment
   *
   * @param customerGUID
   * @param file
   * @return Promise
   */
  async upload_fulfilment_attachment(customerGUID: string, file: File) {
    const targetUrl = apiUrl("fulfilment/request/" + customerGUID + "/upload-attachment");
    const fetchOptions = this.createFetchOptions({}, "json", {}, "");

    const formData = new FormData();
    formData.append("attachment", file, file.name);

    return await this.http.post(targetUrl, formData, fetchOptions).toPromise();
  }

  selfServiceCommand(cmd: SelfServiceCommand): Promise<ModifyResponse | null> {
    const targetUrl = apiUrl(`fulfilment/subscription${cmd.uri}`);
    const fetchOptions = this.createFetchOptions();
    return this.http
      .post<ModifyResponse>(targetUrl, cmd.json, fetchOptions)
      .toPromise() as Promise<ModifyResponse | null>;
  }

  set_minimal_impact_notification(customerGUID: string, subscriptionId: string, impact: string) {
    const targetUrl = apiUrl("minimal_impact_notifications/");
    const fetchOptions = this.createFetchOptions();
    const parameters = {
      customerId: customerGUID,
      subscriptionId,
      impact,
    };
    return this.http.post(targetUrl, parameters, fetchOptions).toPromise();
  }

  reportIncidentDomeinen(body: any, currentCustomer: string) {
    const targetUrl = apiUrl(`fulfilment/incident-report/${currentCustomer}/domeinen`);
    const fetchOptions = this.createFetchOptions();
    return this.http.post(targetUrl, body, fetchOptions).toPromise();
  }
}
